// Header menu configuration
import type { PortalLinks } from './portalLinks';

export interface SubMenuItem {
  labelKey: string;
  labelOverseasKey?: string;
  getLink: (links: PortalLinks, isOverseas: boolean, language?: string) => string;
}

export interface MenuItem {
  id: string;
  label: string;
  labelOverseasKey?: string;
  showForOverseas?: boolean;
  getLink: (links: PortalLinks, isOverseas: boolean, language?: string) => string;
  subItems?: SubMenuItem[];
}

export const mainMenuItems: MenuItem[] = [
  {
    id: 'products',
    label: 'Products',
    labelOverseasKey: 'header.menu.products',
    getLink: links => links.products,
    subItems: [
      {
        labelKey: 'menu.hwpSdk',
        labelOverseasKey: 'menu.officeSdk',
        getLink: (links, isOverseas) =>
          isOverseas ? links.productsDetailEn.office : links.productsDetail.hwp,
      },
      {
        labelKey: 'menu.hcellSdk',
        labelOverseasKey: 'menu.spreadsheetSdk',
        getLink: (links, isOverseas) =>
          isOverseas ? links.productsDetailEn.spreadsheet : links.productsDetail.hcell,
      },
      {
        labelKey: 'menu.docdiffSdk',
        getLink: (links, isOverseas) =>
          isOverseas ? links.productsDetailEn.docscompare : links.productsDetail.docs,
      },
      {
        labelKey: 'menu.ocrSdk',
        getLink: (links, isOverseas) =>
          isOverseas ? links.productsDetailEn.ocr : links.productsDetail.ocr,
      },
      {
        labelKey: 'menu.authSdk',
        getLink: (links, isOverseas) =>
          isOverseas ? links.productsDetailEn.auth : links.productsDetail.auth,
      },
    ],
  },
  {
    id: 'service',
    label: 'Service',
    labelOverseasKey: 'header.menu.service',
    showForOverseas: false,
    getLink: links => links.service,
    subItems: [
      {
        labelKey: 'menu.dataloaderApi',
        getLink: links => links.serviceDetail.dataloader,
      },
      {
        labelKey: 'menu.ocrApi',
        getLink: links => links.serviceDetail.ocr,
      },
    ],
  },
  {
    id: 'documents',
    label: 'Documents',
    labelOverseasKey: 'header.menu.documents',
    getLink: (links, isOverseas, language) =>
      isOverseas && language === 'ja' ? links.documentsDetailJa.spreadsheet : links.documents,
    subItems: [
      {
        labelKey: 'menu.hwpSdk',
        labelOverseasKey: 'menu.spreadsheetSdk',
        getLink: (links, isOverseas, language) => {
          if (!isOverseas) return links.documentsDetail.hwp;
          return language === 'ja'
            ? links.documentsDetailJa.spreadsheet
            : links.documentsDetailEn.spreadsheet;
        },
      },
      {
        labelKey: 'menu.hcellSdk',
        labelOverseasKey: 'menu.ocrSdk',
        getLink: (links, isOverseas, language) => {
          if (!isOverseas) return links.documentsDetail.hcell;
          return language === 'ja' ? links.documentsDetailJa.ocr : links.documentsDetailEn.ocr;
        },
      },
      // Only show these for domestic users
      ...([] as SubMenuItem[]).concat([
        {
          labelKey: 'menu.docdiffSdk',
          getLink: links => links.documentsDetail.docs,
        },
        {
          labelKey: 'menu.ocrSdk',
          getLink: links => links.documentsDetail.ocr,
        },
        {
          labelKey: 'menu.dataloaderApi',
          getLink: links => links.documentsDetail.dataloaderApi,
        },
        {
          labelKey: 'menu.ocrApi',
          getLink: links => links.documentsDetail.ocrApi,
        },
      ]),
    ],
  },
  {
    id: 'liveDemo',
    label: 'Live Demo',
    labelOverseasKey: 'header.menu.liveDemo',
    getLink: (links, isOverseas) =>
      isOverseas ? links.liveDemoDetailEn.spreadsheet : links.liveDemo,
    subItems: [
      {
        labelKey: 'menu.hwpSdk',
        labelOverseasKey: 'menu.spreadsheetSdk',
        getLink: (links, isOverseas) =>
          isOverseas ? links.liveDemoDetailEn.spreadsheet : links.liveDemoDetail.hwp,
      },
      {
        labelKey: 'menu.hcellSdk',
        labelOverseasKey: 'menu.ocrSdk',
        getLink: (links, isOverseas) =>
          isOverseas ? links.liveDemoDetailEn.ocr : links.liveDemoDetail.hcell,
      },
      // Only show these for domestic users
      ...([] as SubMenuItem[]).concat([
        {
          labelKey: 'menu.docdiffSdk',
          getLink: links => links.liveDemoDetail.docs,
        },
        {
          labelKey: 'menu.ocrSdk',
          getLink: links => links.liveDemoDetail.ocr,
        },
        {
          labelKey: 'menu.dataloaderApi',
          getLink: links => links.liveDemoDetail.dataloader,
        },
      ]),
    ],
  },
  {
    id: 'purchase',
    label: 'Purchase',
    labelOverseasKey: 'header.menu.purchase',
    getLink: links => links.purchase,
  },
  {
    id: 'contact',
    label: 'Contact Us',
    labelOverseasKey: 'header.menu.contact',
    getLink: links => links.contact,
  },
];

// Filter submenu items based on region
export const getFilteredSubItems = (
  subItems: SubMenuItem[] | undefined,
  isOverseas: boolean,
  menuId?: string,
): SubMenuItem[] => {
  if (!subItems) return [];

  // Products and Service menus: show all items for both domestic and overseas
  if (menuId === 'products' || menuId === 'service') {
    return subItems;
  }

  // Documents and Live Demo: show only first 2 items (Spreadsheet and OCR) for overseas
  if (isOverseas) {
    return subItems.slice(0, 2);
  }

  // For domestic users, show all items
  return subItems;
};
